import { Calendar, FileDown, FileText, SaudiRiyal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { GetReservationById } from '@virtual-care/client';
import {
  formatReservationDateTime,
  getLocalizedSpeciality,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
} from '@virtual-care/shadcn';
import { ReservationStatus } from '@virtual-care/ui';

import { useLanguage } from '../../../i18n/use-language.ts';
import type { StatusMap } from './status.ts';

interface AppointmentCardProps {
  reservation: GetReservationById;
  statusInfoMap: StatusMap;
  patientSummary: Array<{ label: string; value: string }>;
  onViewAttachments: () => void;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  totalAmount?: string;
}

export function AppointmentCard({
  reservation,
  statusInfoMap,
  patientSummary,
  onViewAttachments,
  children,
  footer,
  totalAmount,
}: AppointmentCardProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const speciality = getLocalizedSpeciality(reservation.speciality, language);
  const doctorName = reservation?.doctor
    ? `${reservation.doctor.firstName} ${reservation.doctor.lastName}`
    : t('common.notSpecified');

  return (
    <Card className="relative overflow-hidden rounded-2xl pb-0">
      <CardHeader className="flex flex-row items-start justify-between px-6">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-6 w-6 text-gray-600" />
          <CardTitle className="font-bold md:text-xl">
            {t('appointments.detail.card.titleWithSpecialty', {
              specialty: speciality.name,
            })}
          </CardTitle>
        </div>
        <div className="absolute inset-e-4 top-6 hidden md:block">
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground font-bold">
                {t('appointments.detail.card.statusLabel')}
              </p>
              <ReservationStatus.Root
                status={reservation.status}
                className="rounded-4xl px-3"
              >
                <ReservationStatus.Indicator className="h-2 w-2" />
                <ReservationStatus.Label render={(meta) => t(meta.key)} />
              </ReservationStatus.Root>
            </div>
            <p>
              <span className="text-primary flex items-center gap-x-1">
                <span className="text-muted-foreground font-bold">
                  {t('appointments.detail.card.priceLabel')}
                </span>
                {totalAmount} <SaudiRiyal className="h-4 w-4" />
              </span>
            </p>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
              <span className="text-xs text-blue-500">
                {t('appointments.detail.card.paid')}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="justify-end space-y-4 gap-x-4 p-4">
        <div className="space-y-2 text-sm">
          <p>
            <span className="font-semibold text-gray-500">
              {t('appointments.detail.card.appointmentNumber')}{' '}
            </span>
            #{reservation.reservationNumber}
          </p>

          <p>
            <span className="font-semibold text-gray-500">
              {t('appointments.detail.card.appointmentDate')}
            </span>{' '}
            {formatReservationDateTime(reservation)}
          </p>

          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-500">
              {t('appointments.detail.card.serviceType')}
            </span>
            <Badge className={cn('bg-tertiary py-1')}>
              {t('common.services.standard')}
            </Badge>
          </div>

          <div className="flex flex-col gap-1 md:hidden">
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground font-bold">
                {t('appointments.detail.card.statusLabel')}
              </p>
              <ReservationStatus.Root
                status={reservation.status}
                className="rounded-md"
              >
                <ReservationStatus.Label render={(meta) => t(meta.key)} />
              </ReservationStatus.Root>
            </div>
            <p>
              <span className="text-muted-foreground font-bold">
                {t('appointments.detail.card.priceLabel')}{' '}
              </span>
              -
            </p>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
              <span className="text-xs text-blue-500">
                {t('appointments.detail.card.paid')}
              </span>
            </div>
          </div>
          <p>
            <span className="font-semibold text-gray-500">
              {t('appointments.detail.card.caseDoctor')}{' '}
            </span>
            {doctorName}
          </p>
        </div>

        {statusInfoMap[reservation.status] && (
          <div
            className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${statusInfoMap[reservation.status].className}`}
          >
            {(() => {
              const Icon = statusInfoMap[reservation.status].icon;
              return <Icon className="h-4 w-4" />;
            })()}
            {statusInfoMap[reservation.status].text}
          </div>
        )}

        {reservation.status === 'AVAILABLE_FOR_FOLLOWUP' && (
          <p className="text-primary">
            {t('appointments.detail.card.followupAvailable')}
          </p>
        )}

        <div className="mt-6 flex items-center gap-1.5">
          <FileText className="h-6 w-6 text-gray-600" />
          <CardTitle className="text-lg font-black">
            {t('appointments.detail.card.attachmentsHeader')}
          </CardTitle>
        </div>

        <div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg px-4">
            {patientSummary.map((item) => (
              <div key={item.label} className="flex items-baseline gap-2">
                <p className="text-foreground text-base font-bold">
                  {item.label}:
                </p>
                <p className="bg-muted text-muted-foreground rounded-lg px-3 py-1">
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          {reservation.files.length > 0 && (
            <Button
              variant="outline"
              className="float-end mt-4 gap-2"
              onClick={onViewAttachments}
            >
              <FileDown className="h-4 w-4" />
              {t('appointments.detail.card.viewAttachments')}
            </Button>
          )}
        </div>

        {children}
      </CardContent>

      <CardFooter className="bg-muted flex items-center justify-between px-6 py-3.5">
        <div />
        {footer}
      </CardFooter>
    </Card>
  );
}
